import { Component, effect, Input, OnChanges } from '@angular/core';
import { NgxEchartsDirective } from 'ngx-echarts';
import { EChartsOption } from 'echarts';
import {
  DASHBOARD_CHART_PALETTES,
  DashboardChartPalette,
  ECHARTS_THEMES,
} from '../../../../shared/services/echarts/echarts.config';
import { ThemeService } from '../../../../shared/services/theme/theme.service';
import { EngagementDonutMetric } from '../../../../shared/models/interfaces/dashboard/dashboard.interface';

export type DashboardDonutVariant = 'full' | 'semi';
export type DashboardDonutSize = 'default' | 'large';

@Component({
  selector: 'l-dashboard-donut-chart',
  standalone: true,
  imports: [NgxEchartsDirective],
  templateUrl: './dashboard-donut-chart.component.html',
  host: { class: 'block' },
})
export class DashboardDonutChartComponent implements OnChanges {

  constructor(private readonly themeService: ThemeService) {
    effect(() => {
      this.palette = DASHBOARD_CHART_PALETTES[this.themeService.theme()];

      if (this.metric) {
        this.chartOptions = this.buildOptions();
      }
    });
  }

  @Input({ required: true })
  public metric!: EngagementDonutMetric;

  @Input()
  public variant: DashboardDonutVariant = 'full';

  @Input()
  public size: DashboardDonutSize = 'default';

  public chartOptions: EChartsOption = {};

  public readonly theme = ECHARTS_THEMES[this.themeService.theme()];

  public get chartHeight(): number {
    if (this.variant === 'semi') {
      return 112;
    }

    return this.size === 'large' ? 176 : 192;
  }

  public get chartWidthStyle(): string {
    if (this.variant === 'semi') {
      return '100%';
    }

    return `${this.chartHeight}px`;
  }

  private palette: DashboardChartPalette = DASHBOARD_CHART_PALETTES[this.themeService.theme()];

  public ngOnChanges(): void {
    this.chartOptions = this.buildOptions();
  }

  private buildDottedRingData(): Array<{ value: number; itemStyle: { color: string } }> {
    const tickCount = 56;
    const tickLength = 1;
    const gapLength = 2.2;

    return Array.from({ length: tickCount }, () => [
      {
        value: tickLength,
        itemStyle: { color: this.palette.dottedRing },
      },
      {
        value: gapLength,
        itemStyle: { color: 'transparent' },
      },
    ]).flat();
  }

  private buildOptions(): EChartsOption {
    const percentage = this.metric.percentage;
    const remaining = 100 - percentage;
    const isSemi = this.variant === 'semi';
    const isLarge = this.size === 'large';
    const showAbsolute = this.metric.absoluteValue > 0 && this.metric.absoluteLabel.length > 0;

    return {
      tooltip: {
        trigger: 'item',
        backgroundColor: this.palette.tooltipBackground,
        borderColor: this.palette.tooltipBorder,
        textStyle: { color: this.palette.text },
        formatter: this.buildTooltipFormatter(percentage, showAbsolute),
      },
      series: [
        ...this.buildDottedSeries(isSemi, isLarge),
        this.buildMainPieSeries(percentage, remaining, isSemi, isLarge, showAbsolute),
      ],
    };
  }

  private buildTooltipFormatter(percentage: number, showAbsolute: boolean): string {
    if (showAbsolute) {
      return `${this.metric.absoluteValue} ${this.metric.absoluteLabel}`;
    }

    return `${percentage}%`;
  }

  private buildCenterLabel(percentage: number, showAbsolute: boolean): string {
    if (showAbsolute) {
      return `{percent|${percentage}%}\n{value|${this.metric.absoluteValue} ${this.metric.absoluteLabel}}`;
    }

    return `{percent|${percentage}%}`;
  }

  private buildMainRadius(isSemi: boolean, isLarge: boolean): [string, string] {
    if (isSemi) {
      return ['70%', '90%'];
    }

    return isLarge ? ['68%', '86%'] : ['66%', '84%'];
  }

  private buildLargeDottedRadius(): [string, string] {
    return ['59%', '60.2%'];
  }

  private buildDefaultDottedRadius(): [string, string] {
    return ['57%', '58.2%'];
  }

  private resolvePercentFontSize(isSemi: boolean, isLarge: boolean): number {
    if (isSemi) {
      return 30;
    }

    if (isLarge) {
      return 26;
    }

    return 32;
  }

  private resolvePercentLineHeight(isSemi: boolean, isLarge: boolean): number {
    if (isSemi) {
      return 34;
    }

    if (isLarge) {
      return 30;
    }

    return 38;
  }

  private buildDottedSeries(
    isSemi: boolean,
    isLarge: boolean
  ): Array<NonNullable<EChartsOption['series']> extends Array<infer T> | infer T ? T : never> {
    if (isSemi) {
      return [];
    }

    if (isLarge) {
      return [this.createDottedPieSeries(this.buildLargeDottedRadius())];
    }

    return [this.createDottedPieSeries(this.buildDefaultDottedRadius())];
  }

  private createDottedPieSeries(radius: [string, string]): {
    type: 'pie';
    radius: [string, string];
    center: [string, string];
    silent: boolean;
    z: number;
    animation: boolean;
    label: { show: boolean };
    labelLine: { show: boolean };
    data: Array<{ value: number; itemStyle: { color: string } }>;
  } {
    return {
      type: 'pie' as const,
      radius,
      center: ['50%', '50%'] as [string, string],
      silent: true,
      z: 1,
      animation: false,
      label: { show: false },
      labelLine: { show: false },
      data: this.buildDottedRingData(),
    };
  }

  private buildMainPieSeries(
    percentage: number,
    remaining: number,
    isSemi: boolean,
    isLarge: boolean,
    showAbsolute: boolean
  ): {
    type: 'pie';
    radius: [string, string];
    center: [string, string] | string[];
    startAngle: number;
    endAngle: number;
    avoidLabelOverlap: boolean;
    silent: boolean;
    z: number;
    label: object;
    labelLine: { show: boolean };
    data: Array<{ value: number; name: string; itemStyle: object }>;
  } {
    const centerLabel = this.buildCenterLabel(percentage, showAbsolute);
    const percentFontSize = this.resolvePercentFontSize(isSemi, isLarge);
    const percentLineHeight = this.resolvePercentLineHeight(isSemi, isLarge);

    return {
      type: 'pie',
      radius: this.buildMainRadius(isSemi, isLarge),
      center: isSemi ? ['50%', '88%'] : ['50%', '50%'],
      startAngle: isSemi ? 180 : 90,
      endAngle: isSemi ? 0 : -270,
      avoidLabelOverlap: true,
      silent: true,
      z: 2,
      label: {
        show: true,
        position: 'center',
        formatter: () => centerLabel,
        rich: {
          percent: {
            color: this.palette.text,
            fontSize: percentFontSize,
            fontWeight: 700,
            lineHeight: percentLineHeight,
          },
          value: {
            color: this.palette.subtext,
            fontSize: isLarge ? 14 : 13,
            lineHeight: 18,
            padding: [6, 4, 0, 4],
          },
        },
      },
      labelLine: { show: false },
      data: [
        {
          value: percentage,
          name: this.metric.title,
          itemStyle: {
            color: this.palette.progress,
            borderRadius: 20,
          },
        },
        {
          value: remaining,
          name: 'Restante',
          itemStyle: {
            color: this.palette.track,
          },
        },
      ],
    };
  }

}
